"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useConsent } from "./consent-provider"

export function CookieConsent() {
  const { hasConsented, preferences, acceptAll, acceptNecessary, updatePreferences, showModal, setShowModal } = useConsent()
  const [tempPreferences, setTempPreferences] = useState(preferences)

  // Sync temp preferences when modal opens
  useEffect(() => {
    if (showModal) {
      setTempPreferences(preferences)
    }
  }, [showModal, preferences])

  const handleSavePreferences = () => {
    updatePreferences(tempPreferences)
    setShowModal(false)
  }

  return (
    <>
      {/* Cookie Banner */}
      {!hasConsented && !showModal && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-card/95 backdrop-blur-sm border-t border-border"
          role="dialog"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-description"
        >
          <div className="container mx-auto max-w-4xl">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <h3 id="cookie-banner-title" className="font-semibold text-foreground mb-2">
                  Cookie Consent
                </h3>
                <p id="cookie-banner-description" className="text-sm text-muted-foreground">
                  We use cookies to enhance your experience. Analytics cookies are <strong>disabled by default</strong> and only enabled with your explicit consent.
                  <a
                    href="/privacy-policy"
                    className="text-primary hover:underline ml-1"
                  >
                    Learn more
                  </a>
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setTempPreferences(preferences)
                    setShowModal(true)
                  }}
                  className="border-border hover:border-primary/50"
                >
                  Customize
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={acceptNecessary}
                  className="border-border hover:border-primary/50 bg-transparent"
                >
                  Necessary Only
                </Button>
                <Button
                  size="sm"
                  onClick={acceptAll}
                  className="bg-primary hover:bg-primary/80"
                >
                  Accept All
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Dialog */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Cookie Preferences</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Choose which cookies you want to accept. Analytics and marketing cookies are <strong>opt-in only</strong> and will not be set without your explicit consent.
            </p>

            <div className="space-y-4">
              {/* Necessary */}
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <h4 className="font-medium text-foreground">Necessary Cookies</h4>
                  <p className="text-sm text-muted-foreground">
                    Essential for the website to function properly. Cannot be disabled.
                  </p>
                </div>
                <div className="text-sm text-muted-foreground">Always Active</div>
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <h4 className="font-medium text-foreground">Analytics Cookies</h4>
                  <p className="text-sm text-muted-foreground">
                    Help us understand how visitors interact with our website (Vercel Analytics).
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tempPreferences.analytics}
                    onChange={(e) =>
                      setTempPreferences((prev) => ({
                        ...prev,
                        analytics: e.target.checked,
                      }))
                    }
                    className="sr-only peer"
                    aria-label="Enable analytics cookies"
                  />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              {/* Marketing */}
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <h4 className="font-medium text-foreground">Marketing Cookies</h4>
                  <p className="text-sm text-muted-foreground">
                    Used to track visitors across websites for marketing purposes.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tempPreferences.marketing}
                    onChange={(e) =>
                      setTempPreferences((prev) => ({
                        ...prev,
                        marketing: e.target.checked,
                      }))
                    }
                    className="sr-only peer"
                    aria-label="Enable marketing cookies"
                  />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSavePreferences} className="bg-primary hover:bg-primary/80">
                Save Preferences
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}